import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { track } from "../../lib/analytics";
import { IS_SAAS_HOST } from "../../lib/api";

// On a custom SaaS domain the homepage lives at "/"; on the PGT domain it lives at "/software".
export const HOME = IS_SAAS_HOST ? "/" : "/software";

const U = (id, w = 1600) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;
export const IMG = {
  heroGym: U("photo-1637430308606-86576d8fef3c", 1920),
  gymFloor: U("photo-1728486145245-d4cb0c9c3470"),
  fitness: U("photo-1590487988256-9ed24133863e"),
  arena: U("photo-1771909719482-4f95e62f41a7"),
  turf: U("photo-1610729866389-fbf90649c302"),
  turfStripe: U("photo-1522947961977-67fe74257c16"),
};

// Canonical/OG always point at the primary SaaS marketing domain (never the preview/emergent host).
export const SAAS_CANONICAL = (process.env.REACT_APP_SAAS_CANONICAL || "https://aiturfdesigner.com").replace(/\/$/, "");

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

export function useSeo(title, description) {
  useEffect(() => {
    document.title = title;
    const path = window.location.pathname;
    // homepage (served at "/" on the SaaS domain and at "/software" on the PGT domain) canonicalises to root
    const cleanPath = (path === "/" || path === "/software" || path === "/saas") ? "" : path;
    const url = SAAS_CANONICAL + cleanPath;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "AI Turf Designer");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
  }, [title, description]);
}

export const SITE = {
  name: "AI Turf Designer",
  url: SAAS_CANONICAL,
  description: "AI-powered turf designer software for turf installers, gym flooring and sports facility companies. Let customers design custom gym turf, get an instant price and request a quote.",
};

// Injects JSON-LD structured data into <head> (removed on unmount).
export function JsonLd({ id, data }) {
  useEffect(() => {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.text = JSON.stringify(data);
    document.head.appendChild(s);
    return () => { const e = document.getElementById(id); if (e) e.remove(); };
  }, [id, JSON.stringify(data)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const font = { display: "Barlow Condensed, sans-serif", body: "Manrope, sans-serif" };

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }} className={className}>{children}</motion.div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`text-[#00D26A] text-xs font-semibold uppercase tracking-[0.32em] ${className}`}>{children}</div>
);

export const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-6 md:px-12 ${className}`}>{children}</div>
);

export function TrialButton({ source = "cta", children = "Start Your 14-Day Free Trial", className = "", variant = "primary", testid = "start-trial-btn" }) {
  const nav = useNavigate();
  const go = () => { track("signup_clicked", { source }); nav(`/signup?source=${encodeURIComponent(source)}`); };
  const base = "inline-flex items-center justify-center font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#00D26A]";
  const styles = variant === "primary"
    ? "bg-[#00D26A] text-black hover:bg-[#00E574] shadow-[0_0_40px_rgba(0,210,106,0.25)]"
    : "bg-transparent text-white border border-white/20 hover:bg-white/5";
  return <button data-testid={testid} onClick={go} className={`${base} ${styles} ${className}`} style={{ fontFamily: font.body }}>{children}</button>;
}

const NAV = [
  { to: HOME, label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/demo", label: "Live Demo" },
  { to: "/contact", label: "Book a Demo" },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0a0a]/85 border-b border-white/10" data-testid="marketing-nav">
      <Container className="flex items-center justify-between h-16">
        <Link to={HOME} className="flex items-center gap-2" data-testid="nav-logo">
          <span className="w-2.5 h-2.5 rounded-sm bg-[#00D26A] shadow-[0_0_12px_rgba(0,210,106,0.8)]" />
          <span className="text-white font-black uppercase tracking-tight text-lg leading-none" style={{ fontFamily: font.display }}>AI Turf Designer</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => {
            const cls = "text-sm text-zinc-400 hover:text-white transition-colors font-medium";
            const tid = `nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`;
            return n.to === "/demo"
              ? <a key={n.to} href={n.to} data-testid={tid} className={cls}>{n.label}</a>
              : <Link key={n.to} to={n.to} data-testid={tid} className={cls}>{n.label}</Link>;
          })}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm text-zinc-400 hover:text-white transition-colors font-medium" data-testid="nav-signin">Sign in</Link>
          <TrialButton source="nav" className="!px-5 !py-2.5 !text-xs" testid="nav-start-trial">Start Free Trial</TrialButton>
        </div>
        <button className="md:hidden text-white p-2" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
      </Container>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0a0a0a] px-6 py-4 space-y-3" data-testid="nav-mobile-menu">
          {NAV.map((n) => n.to === "/demo"
            ? <a key={n.to} href={n.to} className="block text-zinc-300 py-1">{n.label}</a>
            : <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-zinc-300 py-1">{n.label}</Link>)}
          <Link to="/login" className="block text-zinc-300 py-1">Sign in</Link>
          <TrialButton source="nav-mobile" className="w-full mt-2">Start Free Trial</TrialButton>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0a]" data-testid="marketing-footer">
      <Container className="py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <div className="text-white font-black uppercase tracking-tight text-lg" style={{ fontFamily: font.display }}>AI Turf Designer</div>
          <p className="text-zinc-500 text-sm mt-3 leading-relaxed">Turn your turf website into an interactive sales tool. Let customers design, price and enquire — before they buy.</p>
        </div>
        <FooterCol title="Product" links={[["Home", HOME], ["Pricing", "/pricing"], ["Live Demo", "/demo"], ["Book a Demo", "/contact"]]} />
        <FooterCol title="Solutions" links={[["Gym Turf Designer", "/gym-turf-designer"], ["Artificial Turf Designer", "/artificial-turf-designer"], ["Gym Flooring Software", "/gym-flooring-software"], ["Turf Visualiser", "/turf-visualiser"]]} />
        <FooterCol title="Account" links={[["Start Free Trial", "/signup"], ["Sign in", "/login"]]} />
      </Container>
      <div className="border-t border-white/10">
        <Container className="py-6 text-xs text-zinc-600 flex flex-col md:flex-row gap-2 md:justify-between">
          <span>© {new Date().getFullYear()} AI Turf Designer. All rights reserved.</span>
          <span className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-[#00D26A] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#00D26A] transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
            <span>Built for turf installers, gym flooring & sports facility companies.</span>
          </span>
        </Container>
      </div>
    </footer>
  );
}

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{title}</div>
    <ul className="space-y-2.5">
      {links.map(([l, to]) => <li key={to + l}>{to === "/demo"
        ? <a href={to} className="text-sm text-zinc-400 hover:text-[#00D26A] transition-colors">{l}</a>
        : <Link to={to} className="text-sm text-zinc-400 hover:text-[#00D26A] transition-colors">{l}</Link>}</li>)}
    </ul>
  </div>
);

export function MarketingShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#00D26A] selection:text-black" style={{ fontFamily: font.body }}>
      <JsonLd id="ld-org" data={{
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "Organization", "@id": SITE.url + "/#org", "name": SITE.name, "url": SITE.url, "description": SITE.description,
            "contactPoint": { "@type": "ContactPoint", "contactType": "sales", "url": SITE.url + "/contact" } },
          { "@type": "WebSite", "@id": SITE.url + "/#website", "name": SITE.name, "url": SITE.url, "publisher": { "@id": SITE.url + "/#org" } },
        ],
      }} />
      <Nav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

export const FONT = font;
